import type { ReactNode } from "react";
import type { AvatarGroupSize } from "@design-system/Avatar/AvatarGroup";
import { AvatarTooltip } from "./AvatarTooltip";
import { LabelledAvatarGroup } from "./LabelledAvatarGroup";
import {
    WorkspaceAvatar,
    type WorkspaceAvatarWorkspace,
} from "@design-system/Avatar/WorkspaceAvatar";

export interface WorkspaceAvatarGroupItem {
    workspace: WorkspaceAvatarWorkspace & { id: string };
    /** A line under the name in the tooltip, e.g. the member's role. */
    tooltip?: ReactNode;
}

export interface WorkspaceAvatarGroupProps {
    workspaces: readonly WorkspaceAvatarGroupItem[];
    /** Visible avatars before the +N chip (see `AvatarGroup`). @default 4 */
    max?: number;
    /** @default "xs" */
    size?: AvatarGroupSize;
    /** Trailing text after the stack, e.g. "3 clients". */
    label?: ReactNode;
    className?: string;
}

/**
 * A stack of workspace (client) avatars, each with its name, and the
 * optional `tooltip` line, in a tooltip.
 */
export const WorkspaceAvatarGroup = ({
    workspaces,
    max,
    size,
    label,
    className,
}: Readonly<WorkspaceAvatarGroupProps>) => (
    <LabelledAvatarGroup
        items={workspaces}
        getKey={({ workspace }) => workspace.id}
        getLabel={({ workspace }) => workspace.name}
        renderAvatar={({ workspace, tooltip }) => {
            const hasTooltipLine = tooltip !== undefined && tooltip !== null;
            return (
                <AvatarTooltip
                    title={hasTooltipLine ? workspace.name : undefined}
                    content={hasTooltipLine ? tooltip : workspace.name}
                >
                    <WorkspaceAvatar workspace={workspace} />
                </AvatarTooltip>
            );
        }}
        max={max}
        size={size}
        label={label}
        className={className}
    />
);
